import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import {Box, Grid} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useFormik} from 'formik';
import {
    useStyles,
} from './NewPhoneBookEntryDialog.utils';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {Permission} from '../../store/types/Permission';
import DialogContainer, {DialogButton} from '../../components/AlertDialog/DialogContainer';
import {PhoneDirectoryRecord} from '../../store/types/Devices';
import CustomizedTextField from '../../components/TextField/TextField';
import { addDevicePhoneBookValidationSchema, NewPhoneBookEntryDialogProps } from './NewPhoneBookEntryDialog.utils';
import { actions } from '../../store';
import { showToast } from '../../utils/showToast';
import { ReduxState } from '../../store/types';

const NewPhoneBookEntryDialog: React.FC<NewPhoneBookEntryDialogProps> = ({
        isOpen,
        clickOnClose,
        editRecord,
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        phoneBookDetails,
    } = useSelector((state: ReduxState) => state.devices);

    const initialValues: PhoneDirectoryRecord = useMemo(
        () => (editRecord ? editRecord : {
            first_name: '',
            i_ua_config_directory_item: 0,
            last_name: '',
            mobile_number: '',
            office_number: '',
            other_number: '',
        } as PhoneDirectoryRecord),
        [editRecord],
    );

    const doSubmitDate = useCallback((form: PhoneDirectoryRecord) => {
        if(phoneBookDetails && form) {
            const records = [...(phoneBookDetails?.directory_records ?? [])];
            const mappedObject = {
                ...form
            };
            if(editRecord)
            {
                const indx = records.findIndex(e => e.i_ua_config_directory_item === form.i_ua_config_directory_item);
                records[indx] = form;
            }
            else
            {
                //@ts-ignore
                mappedObject.i_ua_config_directory_item = undefined;
                records.push(mappedObject);
            }
            dispatch(actions.updatePhoneBookDetails.request({
                ...phoneBookDetails,
                total_records: editRecord ? phoneBookDetails.total_records : phoneBookDetails.total_records + 1,
                directory_records: records,
                callback: () => {
                    clickOnClose?.(true);
                    const toast = editRecord
                        ? t<string>('screens:devicePhoneBooks.devicePhoneBookRecordEdited')
                        : t<string>('screens:devicePhoneBooks.devicePhoneBookRecordAdded');
                    showToast(toast);
                }
            }));
        }
    }, [phoneBookDetails, editRecord, clickOnClose]);

    const {
        values,
        handleSubmit,
        resetForm,
        errors,
        setFieldError,
        handleChange,
        dirty
    } = useFormik<PhoneDirectoryRecord>({
        initialValues,
        onSubmit: (form) => {
            doSubmitDate(form);
        },
        validationSchema: addDevicePhoneBookValidationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true
    });

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const isDisabled = (): boolean => {
        return (
            !dirty ||
            !values.first_name ||
            !values.office_number ||
            !values.first_name?.length ||
            !values.office_number?.length)
    }
    
    return (
        <PermissionProvider permission={Permission.Inventory.DevicePhoneBooks.value}>
            <DialogContainer
                className={classes.modalContainer}
                isOpen={isOpen}
                dataQa={'devicephonebookentry-add-new-modal'}
                header={isOpen
                    ? editRecord
                        ? t<string>('screens:devicePhoneBooks.editEntryDialogTitle')
                        : t<string>('screens:devicePhoneBooks.addEntryDialogTitle')
                    : '  '}
                headerClass={classes.header}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            clickOnClose?.(false);
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:save')}
                        disabled={
                            isDisabled()
                        }
                        onClick={handleSubmit}
                        dataQa={'devicephonebookentry-save-button'}
                        primary
                    />,
                ]}
            >
                <form
                    className={classes.form}
                    autoComplete="off"
                    data-testid="devicephonebookentry-form"
                >
                    <Grid className={classes.itemsContainer}>
                        <Box className={classes.boxRow}>
                            <CustomizedTextField
                                id="first_name"
                                label={t('screens:devicePhoneBooks.firstName')}
                                value={values.first_name}
                                helperText={
                                    errors.first_name && errors.first_name.length > 0
                                        ? errors.first_name
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                required
                                maxLength={64}
                                dataQa="deviceprofile-first-name-field"
                            />
                            <CustomizedTextField
                                id="last_name"
                                label={t('screens:devicePhoneBooks.lastName')}
                                value={values.last_name}
                                helperText={
                                    errors.last_name && errors.last_name.length > 0
                                        ? errors.last_name
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={64}
                                dataQa="deviceprofile-last-name-field"
                            />
                            <CustomizedTextField
                                id="mobile_number"
                                label={t('screens:devicePhoneBooks.mobileNumber')}
                                value={values.mobile_number}
                                helperText={
                                    errors.mobile_number && errors.mobile_number.length > 0
                                        ? errors.mobile_number
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={64}
                                dataQa="deviceprofile-mobile-number-field"
                            />
                            <CustomizedTextField
                                id="office_number"
                                label={t('screens:devicePhoneBooks.officeNumber')}
                                value={values.office_number}
                                helperText={
                                    errors.office_number && errors.office_number.length > 0
                                        ? errors.office_number
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={64}
                                required
                                dataQa="deviceprofile-office-number-field"
                            />
                            <CustomizedTextField
                                id="other_number"
                                label={t('screens:devicePhoneBooks.otherNumber')}
                                value={values.other_number}
                                helperText={
                                    errors.other_number && errors.other_number.length > 0
                                        ? errors.other_number
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={64}
                                dataQa="deviceprofile-other-number-field"
                            />
                        </Box>
                    </Grid>
                </form>
            </DialogContainer>
        </PermissionProvider>
    );
};

export default NewPhoneBookEntryDialog;
